<script setup lang="ts">
import { ref, computed } from 'vue'
import WorkcardGalleryAccordian from 'Modules/pages/workcard/WorkcardGalleryAccordian.vue'
import WorkcardGalleryGrid from 'Modules/pages/workcard/WorkcardGalleryGrid.vue'
import { MagnifyingGlassIcon } from 'Common/components/elements/icons/Icons'
import computeImageDetailStats from 'Modules/pages/workcard/computeImageDetailStats'
import type { WorkcardStatsType } from 'Modules/types/pages/workcard/WorkcardStatsType'
import type { ImagesType } from 'Modules/types/generic/image/ImagesType'

const emit = defineEmits<{
  onImagesLoaded: [boolean]
}>()

interface PropTypes {
  summaryImageDetailsObj: ImagesType
  workcardStats?: WorkcardStatsType
  inspectionInstance: string
}

const search = ref('')
const collapseTitleMapping = {
  incomplete: 'Incomplete',
  complete: 'Complete',
}
const props = defineProps<PropTypes>()
const filtered = computed<ImagesType>(() => {
  const rtnObj = {
    incomplete: null,
    complete: null,
  }
  if (search.value === '') return props.summaryImageDetailsObj
  for (const key in props.summaryImageDetailsObj) {
    const val = props.summaryImageDetailsObj[key]
    Array.isArray(val) &&
      (rtnObj[key] = Array.from(val).filter((detail) => detail.imageName.toLowerCase().includes(search.value)))
  }
  return rtnObj
})
const stats = computed<WorkcardStatsType>(() => {
  return computeImageDetailStats(filtered.value)
})
const activeCollapseItems = computed<string[]>(() => {
  const collapseKeys = Object.keys(collapseTitleMapping)
  const rtnArr = []
  for (const key in filtered.value) {
    if (collapseKeys.includes(key)) {
      const imgDetail = filtered.value[key]
      if (imgDetail.length > 0) rtnArr.push(collapseTitleMapping[key])
    }
  }
  return rtnArr
})

let imagesLoadedCounter = 0
const handleImagesLoaded = () => {
  const loadedTotal = Object.keys(filtered.value).reduce((a, c) => a + (filtered.value[c].length ? 1 : 0), 0)
  imagesLoadedCounter++
  if (imagesLoadedCounter >= loadedTotal) {
    emit('onImagesLoaded', true)
  }
}
</script>

<template>
  <div class="border border-solid border-neutral-300 rounded grid grid-rows-[2.5em_auto] grid-cols-1">
    <div class="bg-neutral-300 flex items-center justify-between">
      <span class="grow text-left text-lg font-semibold px-3 py-1">Images</span>
      <div class="relative px-3 py-6 flex items-center justify-center">
        <div>
          <el-input
            v-model="search"
            :prefix-icon="MagnifyingGlassIcon"
            placeholder="Search Images"
            clearable
            size="small"
          />
        </div>
      </div>
    </div>
    <div id="workcard-gallery-accordians">
      <el-collapse v-model="activeCollapseItems">
        <WorkcardGalleryAccordian
          color="yellow-300"
          :value="stats.incompleteImages"
          :max="stats.totalImages"
          :title="collapseTitleMapping['incomplete']"
        >
          <WorkcardGalleryGrid
            :workcard-images-detail-list="filtered.incomplete"
            :inspection-instance="props.inspectionInstance"
            image-loading="eager"
            @on-images-loaded="handleImagesLoaded"
          />
        </WorkcardGalleryAccordian>
        <WorkcardGalleryAccordian
          color="green-400"
          :value="stats.completeImages"
          :max="stats.totalImages"
          :title="collapseTitleMapping['complete']"
        >
          <WorkcardGalleryGrid
            :workcard-images-detail-list="filtered.complete"
            :inspection-instance="props.inspectionInstance"
            image-loading="eager"
            @on-images-loaded="handleImagesLoaded"
          />
        </WorkcardGalleryAccordian>
      </el-collapse>
    </div>
  </div>
</template>
